import { apiClient } from '@/services/_shared/apiClient';
import ENDPOINTS from '@/utils/endpoints';

// ─── Response Types ──────────────────────────────────────────────────

export interface WalletBalance {
  id: string;
  user_id: string;
  wallet_type: string;
  total_balance: number;
  available_balance: number;
  held_balance: number;
  pending_balance: number;
  balance?: number;
  escrow_hold?: number;
  total_earned: number;
  total_spent: number;
  currency: string;
  status: string;
  last_transaction_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DepositTransaction {
  id: string;
  type: string;
  amount: number;
  fee: number;
  net_amount: number;
  status: string;
  gateway: string;
  gateway_transaction_id: string;
  currency: string;
  description: string;
  created_at: string;
  completed_at: string | null;
  failed_at: string | null;
}

// ─── Airwallex Deposit Types ────────────────────────────────────────
// Backend returns `env: "sandbox" | "production"`. The @airwallex/components-sdk
// expects `env: "demo" | "prod"` — map via `airwallexSdkEnv()` below.

export type AirwallexBackendEnv = 'sandbox' | 'production';
export type AirwallexSdkEnv = 'demo' | 'prod';

/** Map backend env string to SDK env string. One canonical place to keep in sync. */
export function airwallexSdkEnv(backendEnv: AirwallexBackendEnv | string): AirwallexSdkEnv {
  return backendEnv === 'production' ? 'prod' : 'demo';
}

export interface DepositMethod {
  type: string;
  display_name: string;
  min_amount?: number;
  max_amount?: number;
  currencies?: string[];
  icon?: string;
}

export interface AvailableMethodsResponse {
  gateway: string;
  currency: string;
  env: AirwallexBackendEnv;
  methods: DepositMethod[];
  available_gateways: string[];
}

export interface DepositIntentRequest {
  amount: number;
  currency?: string;
  return_url?: string;
  cancel_url?: string;
  customer_id?: string;
}

export interface DepositIntentResponse {
  gateway: 'airwallex';
  intent_id: string;
  /** Single-use secret the Drop-in SDK needs. Never persist. */
  client_secret: string;
  payment_id: string;
  local_id: string;
  transaction_id: string;
  amount: number;
  fee: number;
  currency: string;
  status: string;
  env: AirwallexBackendEnv;
  /** Legacy field kept for the /initiate alias; null for Airwallex. */
  approval_url: string | null;
}

export interface DepositHostedResponse {
  gateway: 'airwallex';
  intent_id: string;
  transaction_id: string;
  /** Redirect-fallback checkout URL (checkout.airwallex.com or checkout-demo). */
  url: string;
  amount: number;
  currency: string;
}

/**
 * Convenience shape surfaced by GET /v1/payments/intents/{id}. The
 * backend credits the wallet idempotently when `credited:true`; this
 * endpoint is safe to poll from the FE until that flips, or until
 * `status` reaches a terminal failure state.
 */
export interface DepositIntentStatus {
  intent_id: string;
  status: string;
  amount: number;
  captured_amount: number;
  refunded_amount: number;
  currency: string;
  method_type: string | null;
  card_brand: string | null;
  credited: boolean;
  transaction_id: string | null;
  settled_at: string | null;
}

export interface StripeIntentResponse {
  payment_intent_id: string;
  client_secret: string;
  transaction_id: string;
  amount: number;
  fee: number;
  net_amount: number;
  status: string;
  requires_action: boolean;
}

export interface StripeConfirmResponse {
  transaction_id: string;
  amount: number;
  new_balance: number;
  status: string;
}

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

interface PaginatedApiResponse<T> {
  status: string;
  data: {
    current_page: number;
    data: T[];
    total: number;
    per_page: number;
    last_page: number;
  };
}

// ─── API Functions ───────────────────────────────────────────────────

/**
 * Get wallet balance for the authenticated user
 */
export async function getWalletBalance(): Promise<WalletBalance> {
  const response = await apiClient.get<ApiResponse<WalletBalance>>(ENDPOINTS.WALLET_BALANCE);
  return response.data.data;
}

/**
 * Get wallet transactions
 */
export async function getWalletTransactions(params?: {
  per_page?: number;
  page?: number;
  type?: string;
  status?: string;
}): Promise<{
  transactions: DepositTransaction[];
  total: number;
  last_page: number;
}> {
  const response = await apiClient.get<PaginatedApiResponse<DepositTransaction>>(
    ENDPOINTS.WALLET_TRANSACTIONS,
    { params }
  );
  return {
    transactions: response.data.data.data,
    total: response.data.data.total,
    last_page: response.data.data.last_page,
  };
}

/**
 * Fetch the methods the active gateway (Airwallex) currently offers for
 * the given currency. Backend returns `env` which the FE must pass to
 * the Airwallex SDK init (via `airwallexSdkEnv`).
 */
export async function getAvailableDepositMethods(
  currency = 'USD'
): Promise<AvailableMethodsResponse> {
  const response = await apiClient.get<ApiResponse<AvailableMethodsResponse>>(
    ENDPOINTS.DEPOSIT_METHODS_AVAILABLE,
    { params: { currency } }
  );
  return response.data.data;
}

/**
 * Create an Airwallex payment intent. The returned `client_secret` is
 * single-use, scoped to this intent, and must be handed to the Drop-in
 * element. Backend persists the intent + a local transaction row; the
 * wallet is only credited after Airwallex reports SUCCEEDED (via
 * webhook, or via `GET /payments/intents/{id}` as a convenience path).
 */
export async function createDepositIntent(
  payload: DepositIntentRequest
): Promise<DepositIntentResponse> {
  const response = await apiClient.post<ApiResponse<DepositIntentResponse>>(
    ENDPOINTS.DEPOSIT_INTENT,
    payload
  );
  return response.data.data;
}

/**
 * Hosted-checkout fallback. Redirect the browser to `data.url`; the user
 * returns via `return_url`. Only use when the embedded Drop-in path is
 * not viable.
 */
export async function createDepositHosted(
  payload: DepositIntentRequest
): Promise<DepositHostedResponse> {
  const response = await apiClient.post<ApiResponse<DepositHostedResponse>>(
    ENDPOINTS.DEPOSIT_HOSTED,
    payload
  );
  return response.data.data;
}

/**
 * Poll the backend for a deposit intent's current state. Idempotent — safe
 * to call repeatedly. Reads Airwallex, flips `credited:true` + updates the
 * local transaction once Airwallex says SUCCEEDED.
 */
export async function getDepositIntentStatus(intentId: string): Promise<DepositIntentStatus> {
  const response = await apiClient.get<ApiResponse<DepositIntentStatus>>(
    ENDPOINTS.DEPOSIT_INTENT_STATUS(intentId)
  );
  return response.data.data;
}

/**
 * Create a Stripe payment intent for credit/debit card deposit.
 */
export async function createStripeIntent(
  amount: number,
  paymentMethodId?: string
): Promise<StripeIntentResponse> {
  const response = await apiClient.post<ApiResponse<StripeIntentResponse>>(
    ENDPOINTS.DEPOSIT_STRIPE_INTENT,
    { amount, ...(paymentMethodId && { payment_method_id: paymentMethodId }) }
  );
  return response.data.data;
}

/**
 * Confirm a Stripe deposit after client-side confirmation.
 */
export async function confirmStripeDeposit(
  paymentIntentId: string
): Promise<StripeConfirmResponse> {
  const response = await apiClient.post<ApiResponse<StripeConfirmResponse>>(
    ENDPOINTS.DEPOSIT_STRIPE_CONFIRM,
    { payment_intent_id: paymentIntentId }
  );
  return response.data.data;
}

/**
 * Get deposit history
 */
export async function getDeposits(params?: {
  per_page?: number;
  status?: string;
  gateway?: string;
}): Promise<{ deposits: DepositTransaction[]; total: number }> {
  const response = await apiClient.get<PaginatedApiResponse<DepositTransaction>>(
    ENDPOINTS.DEPOSITS_LIST,
    { params }
  );
  return {
    deposits: response.data.data.data,
    total: response.data.data.total,
  };
}
